import { useEffect, useState } from "react";
import { Badge, Button, Collapse, Drawer, Select, Spin, Tooltip } from "antd";
import { useDropdowns } from "@utils/customHooks";
import HealthemSpinner, { Spinner } from "@components/healthemSpinner/healthemSpinner";
import * as XLSX from "xlsx";
import Icon from "@assets/icon_config";
import "./filter.scss";
import { useMeasuresStore } from "@stores/measuresStore";
import { isEmpty, isEqual, map, size } from "lodash";
import { MultiSelectOpen } from "./multiselect";
import memberDetailsService from "@services/memberDetails.service";
import dashboardService from "@services/dashboard.service";
import React from "react";
import { usePopulationInsightStore } from "@stores/populationInsightStore";
import exportToExcel from "@utils/applyExcelStyle";

interface Option {
  label: any;
  id: string | number;
}

interface FiltersProps {
  columns: any[];
  tableData: any[];
  fetchData: (selectedYear: string, tabKey: string, selectedHealthPlan?: any[], selectedPCPVendor?: any[], selectedProvider?: any[]) => void;
  years: any[];
  selectedYear: any;
  onYearChange: (value: string) => void;
  isDataLoading: boolean;
  tabKey: string;
}

const Filters: React.FC<FiltersProps> = ({ columns, tableData, fetchData, years, selectedYear, onYearChange, isDataLoading, tabKey }) => {
  const { loadingOptions, getOptions, filterDropdowns } = useDropdowns();
  const [loadingPcpVendor, setLoadingPcpVendor] = useState<boolean>(false);
  const [loadingProviders, setLoadingProviders] = useState<boolean>(false);
  const [showLoader, setShowLoader] = useState<boolean>(false);

  const [open, setOpen] = useState<boolean>(false);
  const [flag, setFlag] = useState<boolean>(false);

  const appliedProvider = useMeasuresStore((state) => state.appliedProvider);
  const setAppliedProvider = useMeasuresStore((state) => state.setAppliedProvider);

  const selectedProvider = useMeasuresStore((state) => state.selectedProvider);
  const setSelectedProvider = useMeasuresStore((state) => state.setSelectedProvider);

  const selectedPCPVendor = useMeasuresStore((state) => state.selectedPCPVendor);
  const setSelectedPcpVendor = useMeasuresStore((state) => state.setSelectedPcpVendor);

  const selectedHealthPlan = useMeasuresStore((state) => state.selectedHealthPlan);
  const setSelectedHealthPlan = useMeasuresStore((state) => state.setSelectedHealthPlan);

  const options = useMeasuresStore((state) => state.options);
  const setOptions = useMeasuresStore((state) => state.setOptions);

  const healthPlanOptions = useMeasuresStore((state) => state.healthPlanOptions);
  const setHealthPlanOptions = useMeasuresStore((state) => state.setHealthPlanOptions);

  const pcpVendorOptions = useMeasuresStore((state) => state.pcpVendorOptions);
  const setPcpVendorOptions = useMeasuresStore((state) => state.setPcpVendorOptions);

  const comingFromMembers = usePopulationInsightStore((state) => state.comingFromMembers);

  useEffect(() => {
    getOptions("InsuranceLst");
  }, [getOptions, healthPlanOptions]);

  useEffect(() => {
    if (filterDropdowns?.InsuranceLst && !comingFromMembers) {
      const updatedOptions = map(filterDropdowns.InsuranceLst, (item) => ({
        label: item.value,
        id: item.value,
      }));
      setHealthPlanOptions(updatedOptions);
      setSelectedHealthPlan(map(updatedOptions, (item) => item.id));
    }
  }, [filterDropdowns]);

  const fetchListOfPcpVendor = (payload: any) => {
    setLoadingPcpVendor(true);
    memberDetailsService.fetchAttributeDropdownWithSqParams({ attributeName: "PCPVendorAccToHealthPlan", sqlParamsFromPayload: [payload] }).subscribe({
      next: (response: any) => {
        const updatedOptions = map(response?.data?.data?.data, (item) => ({
          label: item.value,
          id: item.value,
        }));
        setPcpVendorOptions(updatedOptions);
        setSelectedPcpVendor(flag ? appliedProvider[1] : map(updatedOptions, (item) => item.id));
      },
      error: (error) => console.error(error),
      complete: () => setLoadingPcpVendor(false),
    });
  };

  useEffect(() => {
    console.log("selectedHealthPlan", selectedHealthPlan);
    if (!comingFromMembers) {
      if (!isEmpty(selectedHealthPlan)) {
        fetchListOfPcpVendor(selectedHealthPlan);
      } else {
        setSelectedPcpVendor([]);
        setPcpVendorOptions([]);
      }
    }
  }, [selectedHealthPlan]);

  const fetchListOfProviders = (payload: any) => {
    setLoadingProviders(true);
    memberDetailsService.fetchAttributeDropdownWithSqParams({ attributeName: "PCPListAccToPCPVendor", sqlParamsFromPayload: payload }).subscribe({
      next: (response: any) => {
        const updatedOptions = [
          ...map(response?.data?.data?.data, (item) => ({
            label: item.value,
            id: item.key,
          })),
          { id: "NA", label: "NA" },
        ];
        setOptions(updatedOptions);
        setSelectedProvider(flag ? appliedProvider[2] : map(updatedOptions, (item) => item.id));
        setFlag(false);
      },
      error: (error) => console.error(error),
      complete: () => setLoadingProviders(false),
    });
  };

  useEffect(() => {
    if (!comingFromMembers) {
      if (!isEmpty(selectedPCPVendor)) {
        fetchListOfProviders([selectedPCPVendor, selectedHealthPlan]);
      } else {
        setOptions([]);
        setSelectedProvider([]);
      }
    }
  }, [selectedPCPVendor]);

  const handleSelectChange = (value: any) => {
    setSelectedProvider(value);
  };

  const fetchCareGapSummaryInfo = () => {
    const measureIds = map(tableData, "quality_measure_id");
    setShowLoader(true);
    dashboardService.fetchMemberListFromMeasures(measureIds, selectedYear, tabKey, appliedProvider[0], appliedProvider[1], appliedProvider[2]).subscribe({
      next: (response) => {
        const patientIds = response.data.data.patientIds;
        if (!isEmpty(patientIds)) {
          dashboardService.fetchCareGapSummary(patientIds, tabKey).subscribe({
            next: (res) => {
              const summaryData = res.data.data;
              setShowLoader(false);
              exportData(summaryData);
            },
            error: (error) => {
              console.error(error);
              setShowLoader(false);
            },
          });
        } else {
          setShowLoader(false);
          exportData([]);
        }
      },
      error: (error) => {
        console.error(error);
        setShowLoader(false);
      },
    });
  };

  const exportData = (summaryData: any[]) => {
    const isHedisTab = tabKey === 'hedis';
    const isMedicationTab = tabKey === 'medication';
    const sheetSuffix = `PY ${selectedYear}`;
    const sheet2Name = `Summary ${sheetSuffix}`;

    const filename = isHedisTab
      ? "Provider Scorecard.xlsx"
      : isMedicationTab
      ? "Medication Adherence - Care Gap.xlsx"
      : "Pharmacy - Care Gap.xlsx";
  
    const sheet1Name = isHedisTab
      ? `Scorecard ${sheetSuffix}`
      : isMedicationTab
      ? `Medication ${sheetSuffix}`
      : `Pharmacy ${sheetSuffix}`;
    
    const scorecardRows = tableData.map((entry) => {
      const row: any = {};
      columns.forEach((col) => {
        row[col.dataIndex] = entry[col.dataIndex];
      });
  
      if (entry["measure_name"] === "HbA1c Control (<8.0%)" ||
          entry["measure_name"] === "HbA1c Control (>=8.0% & <=9.0%)"
      ) {
        row["care_gaps"] = 0;
      }
    
      return row;
    });

    const totalRow: any = {};
    columns.forEach((col) => {
      const key = col.dataIndex;
  
      if (key === "measure_name") {
        totalRow[key] = "Total";
      } else if (key === "care_gaps") {
        totalRow[key] = scorecardRows.reduce(
          (sum: number, row: any) => sum + (row["care_gaps"] || 0),
          0
        );
      } else {
        totalRow[key] = "";
      }
    });
  
    scorecardRows.push(totalRow);
      const exportSheets: any[] = [
      {
        sheetName: sheet1Name,
        columns: columns.map((col) => ({
          header: col.tableHeaderName,
          key: col.dataIndex,
        })),
        data: scorecardRows,
        styles: {
          headerBold: true,
          boldRows: ["Total"],
        },
      },
    ];
  
    if (summaryData.length > 0) {
      const allKeys = Object.keys(summaryData[0]);

      if (allKeys.includes("patient_id") && allKeys[0] !== "patient_id") {
        allKeys.splice(allKeys.indexOf("patient_id"), 1);
        allKeys.unshift("patient_id");
      }

      const summaryColumns = allKeys.map((key) => ({
        header: key.replace(/_/g, " ").toUpperCase(),
        key: key,
      }));
      const formattedSummary = summaryData.map((item: any) => {
        const row: any = {};
        allKeys.forEach((key, index) => {
          const header = summaryColumns[index]?.header;
          row[key] =
            header === "MEASURES COUNT"
              ? item[key]
              : item[key] === "1"
              ? "Yes"
              : item[key] === "0"
              ? "No"
              : item[key];
        });
        return row;
      });
  
      const noteText = isHedisTab
        ? "Note: The 'Measures Count' column counts unique measures, grouping all HBA1C 'Yes' responses as one."
        : "Note: The 'Measures Count' column counts unique measures.";
  
      exportSheets.push({
        sheetName: sheet2Name,
        columns: summaryColumns,
        data: [noteText, ...formattedSummary],
        styles: { headerBold: true },
      });
    }
  
    exportToExcel({
      filename,
      sheets: exportSheets,
    });
  };
  
  const handleExportMeasureReport = () => {
    fetchCareGapSummaryInfo();
  };

  const { Panel } = Collapse;

  return (
    <>
      <Drawer
        width={375}
        bodyStyle={{ padding: 0 }}
        footer={
          <div style={{ textAlign: "right" }} className="cancel-button">
            <Button
              type="text"
              size="large"
              onClick={() => {
                // setSelectedProvider([...appliedProvider]);
                setOpen(false);
              }}
            >
              Cancel
            </Button>

            <Button
              type="primary"
              onClick={() => {
                setOpen(false);
                fetchData(selectedYear, tabKey, selectedHealthPlan, selectedPCPVendor, selectedProvider);
                setAppliedProvider([selectedHealthPlan, selectedPCPVendor, selectedProvider]);
              }}
              className="apply-filter"
              style={{ height: "40px" }}
              disabled={isEmpty(selectedProvider)}
            >
              Apply Filter
            </Button>
          </div>
        }
        closable
        destroyOnClose
        title="Filters"
        placement="right"
        open={open}
        onClose={() => {
          // setSelectedProvider([...appliedProvider]);
          setOpen(false);
        }}
        maskClosable={false}
        extra={
          <Button
            type="link"
            className="reset-all"
            onClick={() => {
              setSelectedHealthPlan(map(healthPlanOptions, (item) => item.id));
            }}
          >
            Reset All
          </Button>
        }
      >
        <Spin spinning={loadingOptions || loadingPcpVendor || loadingProviders} indicator={<HealthemSpinner />}>
          <div className="filter-section__item">
            <Collapse className="filter-section__item__collapse" expandIconPosition="end">
              <Panel header="Health Plan" key="1">
                <MultiSelectOpen titleHeader="Health Plans" options={healthPlanOptions} value={selectedHealthPlan} handleSelect={setSelectedHealthPlan} />
              </Panel>
              <Panel header="PCP Vendor" key="2">
                <MultiSelectOpen titleHeader="PCP Vendor" options={pcpVendorOptions} value={selectedPCPVendor} handleSelect={setSelectedPcpVendor} />
              </Panel>
              <Panel header="PCP" key="3">
                <MultiSelectOpen titleHeader="PCP" options={options} value={selectedProvider} handleSelect={handleSelectChange} />
              </Panel>
            </Collapse>
          </div>
        </Spin>
      </Drawer>

      <div className="filter-section ml-05">
        {tabKey === "hedis" && (
          <Badge
            dot={!isEmpty(selectedProvider) && (size(selectedHealthPlan) !== size(healthPlanOptions) || size(selectedPCPVendor) !== size(pcpVendorOptions) || size(selectedProvider) !== size(options))}
            offset={[-29, 12]}
          >
            <div
              className="btn-icon"
              onClick={() => {
                if (!isEqual(selectedHealthPlan, appliedProvider[0])) {
                  setSelectedHealthPlan(appliedProvider[0]);
                  setFlag(true);
                }
                if (!isEqual(selectedPCPVendor, appliedProvider[1])) {
                  setSelectedPcpVendor(appliedProvider[1]);
                  setFlag(true);
                }
                if (!isEqual(selectedProvider, appliedProvider[2])) {
                  setSelectedProvider(appliedProvider[2]);
                }
                setOpen(true);
              }}
            >
              <Icon name="filter" />
            </div>
          </Badge>
        )}
        <Spin spinning={isDataLoading} indicator={<HealthemSpinner />}>
          <Select size="large" style={{ width: 130 }} value={selectedYear} onChange={onYearChange}>
            {years.map((year) => (
              <Select.Option key={year.id} value={year.value}>
                PY {year.value}
              </Select.Option>
            ))}
          </Select>
        </Spin>
        <Spinner loading={showLoader}>
          <div className="filter-section__item">
            <Tooltip title={isEmpty(tableData) ? "No Data Available for Download" : "Download XLS"}>
              <div className={`filter-section__item__download${isEmpty(tableData) ? " disable" : " enable"}`} onClick={!isEmpty(tableData) ? handleExportMeasureReport : undefined}>
                <Icon name="report-download" colorOfBrand={isEmpty(tableData) ? "#838EA0" : ""} />
                <span>Download</span>
              </div>
            </Tooltip>
          </div>
        </Spinner>
      </div>
    </>
  );
};

export default Filters;
